import { useCallback } from 'react';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';

const POLL_INTERVAL_MS = 3_000;
const TIMEOUT_MS = 5 * 60 * 1_000;

interface UsePOLabelAsyncParams {
  apiKey: string;
}

interface UsePOLabelAsyncReturn {
  downloadLabelsAsync: (arrivalOrderNumber: string) => Promise<void>;
}

/**
 * Manages asynchronous PO label download via polling.
 * Only handles polling logic and delegates state to the consumer (toast).
 */
function usePOLabelAsync({ apiKey }: UsePOLabelAsyncParams): UsePOLabelAsyncReturn {
  const downloadLabelsAsync = useCallback(
    async (arrivalOrderNumber: string) => {
      const toastId = toast.loading('Generando etiquetas...');

      try {
        const http = initializeHttpProvider();
        const createResponse = await http.historyOrder.labelsAsync({
          arrivalOrderNumber,
          apiKey,
        });
        const { jobId } = createResponse.data;
        const deadline = Date.now() + TIMEOUT_MS;

        while (Date.now() < deadline) {
          await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

          const pollResponse = await http.historyOrder.labelsPollJob({ apiKey, jobId });
          const job = pollResponse.data;

          if (job.progress !== undefined) {
            toast.update(toastId, {
              render: `Generando etiquetas... ${job.progress}%`,
            });
          }

          if (job.status === 'done' && job.url) {
            window.open(job.url, '_blank');
            toast.update(toastId, {
              render: '¡Etiquetas generadas exitosamente!',
              type: 'success',
              isLoading: false,
              autoClose: 4_000,
            });
            return;
          }

          if (job.status === 'error') {
            throw new Error(job.error ?? 'El trabajo de generación de etiquetas falló.');
          }
        }

        throw new Error('Tiempo de espera agotado. Las etiquetas tardaron demasiado en generarse.');
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Ha ocurrido un error al generar las etiquetas.';

        toast.update(toastId, {
          render: message,
          type: 'error',
          isLoading: false,
          autoClose: 6_000,
        });
      }
    },
    [apiKey],
  );

  return { downloadLabelsAsync };
}

export { usePOLabelAsync };
